import {
  CUSTOM_PROPERTY_STATUSES,
  getCustomPropertyStatusLabel,
} from './customPropertyStatuses';
import { lookupListingSummaryForLink } from './linkedListingRollup';

export const LINKED_STATUS_FILTER_ALL = 'all';
export const LINKED_STATUS_FILTER_UNSET = 'unset';

export const LINKED_LISTING_SORT_KEYS = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
  MOST_VIEWS: 'most_views',
  LAST_ACTIVITY: 'last_activity',
  LISTING_REF: 'listing_ref',
};

export const LINKED_LISTING_SORT_OPTIONS = [
  { value: LINKED_LISTING_SORT_KEYS.NEWEST, label: 'Newest first' },
  { value: LINKED_LISTING_SORT_KEYS.OLDEST, label: 'Oldest first' },
  { value: LINKED_LISTING_SORT_KEYS.MOST_VIEWS, label: 'Most views' },
  { value: LINKED_LISTING_SORT_KEYS.LAST_ACTIVITY, label: 'Last activity' },
  { value: LINKED_LISTING_SORT_KEYS.LISTING_REF, label: 'Listing ID' },
];

export function buildLinkedStatusFilterOptions() {
  return [
    { value: LINKED_STATUS_FILTER_ALL, label: 'Any status' },
    ...CUSTOM_PROPERTY_STATUSES.map(({ value, label }) => ({ value, label })),
    { value: LINKED_STATUS_FILTER_UNSET, label: 'No status set' },
  ];
}

export function getLinkedStatusFilterLabel(statusFilter) {
  if (statusFilter === LINKED_STATUS_FILTER_ALL) return null;
  if (statusFilter === LINKED_STATUS_FILTER_UNSET) return 'No status set';
  return getCustomPropertyStatusLabel(statusFilter) || statusFilter;
}

export function listingMatchesStatusFilter(listing, statusFilter) {
  if (statusFilter === LINKED_STATUS_FILTER_ALL) return true;
  const custom = listing?.customPropertyStatus
    ? String(listing.customPropertyStatus).trim()
    : '';
  if (statusFilter === LINKED_STATUS_FILTER_UNSET) return !custom;
  return custom === statusFilter;
}

/** Filter listings-summary rows for rollup band totals. */
export function filterListingsByStatus(listings, statusFilter) {
  if (!Array.isArray(listings) || statusFilter === LINKED_STATUS_FILTER_ALL) {
    return Array.isArray(listings) ? listings : [];
  }
  return listings.filter((listing) => listingMatchesStatusFilter(listing, statusFilter));
}

export function linkedRootMatchesStatusFilter(linkRecord, listingsByKey, statusFilter) {
  if (statusFilter === LINKED_STATUS_FILTER_ALL) return true;
  const listing = lookupListingSummaryForLink(listingsByKey, linkRecord);
  if (!listing) {
    return statusFilter === LINKED_STATUS_FILTER_UNSET;
  }
  return listingMatchesStatusFilter(listing, statusFilter);
}

function parseTime(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? t : 0;
}

/** Sort linked property root rows — nested copies stay under their parent. */
export function sortLinkedPlanRoots(roots, sortKey, { linksByUuid, listingsByKey } = {}) {
  const list = Array.isArray(roots) ? [...roots] : [];
  const getLink = (node) => (node?.uuid && linksByUuid?.get?.(node.uuid)) || null;
  const getListing = (node) => lookupListingSummaryForLink(listingsByKey, getLink(node));

  list.sort((a, b) => {
    switch (sortKey) {
      case LINKED_LISTING_SORT_KEYS.OLDEST:
        return parseTime(a.createdAt) - parseTime(b.createdAt);
      case LINKED_LISTING_SORT_KEYS.MOST_VIEWS: {
        const av = Number(getListing(a)?.galleryViews) || 0;
        const bv = Number(getListing(b)?.galleryViews) || 0;
        if (bv !== av) return bv - av;
        return parseTime(b.createdAt) - parseTime(a.createdAt);
      }
      case LINKED_LISTING_SORT_KEYS.LAST_ACTIVITY: {
        const at = parseTime(getListing(a)?.lastActivityAt);
        const bt = parseTime(getListing(b)?.lastActivityAt);
        if (bt !== at) return bt - at;
        return parseTime(b.createdAt) - parseTime(a.createdAt);
      }
      case LINKED_LISTING_SORT_KEYS.LISTING_REF: {
        const ar = String(getLink(a)?.customer_ref || '');
        const br = String(getLink(b)?.customer_ref || '');
        const cmp = ar.localeCompare(br, undefined, { sensitivity: 'base' });
        if (cmp !== 0) return cmp;
        return parseTime(b.createdAt) - parseTime(a.createdAt);
      }
      case LINKED_LISTING_SORT_KEYS.NEWEST:
      default:
        return parseTime(b.createdAt) - parseTime(a.createdAt);
    }
  });
  return list;
}
